import { Post } from '../models/core.models';
import { DimensionAttachTypeId } from '../constants';

import { Observable, shareReplay } from 'rxjs';

import { RecurrenceDefinition } from '../models/schedule.models';
import { Injectable, inject } from '@angular/core';
import { FeedApiService } from 'src/app/api/services/feed-api.service';

export interface CategoriesMap {
  [key: number]: number[];
}

export interface QueryHistoryRecord {
  saved_at: number; //unix timestamp
  query: string; // the query string
  saved_by: string; // display name of the user
}

export interface FeedOperatorHint {
  saved_at: number; //unix timestamp
  name: string; // the query operator string
  description: string; // description of the operator
}

/**
 * Interface for the payload structure.
 */
export interface ManageDimensionPayload {
  id?: string; // Optional dimension ID
  title: string;
  titleI18n?: Record<string, string>; // Internationalized titles
  editMode?: 'simple' | 'advanced';
  simple?: Array<{ text: string }>; // Tokens for simple query
  advanced?: string; // Advanced query string
  defaultColor?: string; // Default color
  workspaceId: string;
  description?: string;
}

export interface DimensionAttachParams {
  object_id: string;
  object_class_id: number;
  attach_type_id: DimensionAttachTypeId;
}

export interface LivePreviewResponse {
  posts: Array<Post>;
  count_posts: number;
}

// * Auto-Story related interfaces - Start
export interface AutoStoryPayload {
  prompt_template_id: number;
  prompt_params?: { [key: string]: any };
  model?: string; // Optional OpenAI model override. When empty/omitted, the model defined on the selected prompt template is used.
  clusters: number;
  distance_threshold: number;
  sanitize_mentions: boolean;
  sanitize_urls: boolean;
  sort: string; // Defines the column name which will be used to sort the resulting clusters. Must define a numeric metric which can be accumulated for the entirety of a given cluster. Only sensible when sort_by is equal to 'column'.
  sample_metric: string; // Sets the metric used to create groups based on percentile rank within each cluster. Can be 'volume' (posts sharing the content hash), 'published' (newest first) or a column name in the export file that contains numeric values. Empty disables sampling.
  sampling_percentile_groups: number; // Defines the number of groups to split the documents in based on percentile rank. Only sensible to define when sample_metric is passed.
  sampling_percentage_per_group: number; // Sets the percentage of documents from each percentile group which will be sampled. Must be a value between 0 and 100. Only sensible to define when sample_metric is passed.
  sampling_time_buckets: boolean; // Enables first-level buckets by time published; the percentile sampling then runs within each bucket. Only sensible to define when sample_metric is passed.
  sample_length: number; // Controls the minimum length required for a document to have in order to be included in the sampling pool for its cluster. Only sensible to define when sample_metric is passed.
  create_story_posts: boolean; // Enables stage 11: create story posts from the clustered topics. Defaults to true on the backend when omitted.
  send_notification: boolean; // Enables stage 12: send the results notification by email. Defaults to true on the backend when omitted.
  schedule?: boolean;
  schedule_interval_minutes?: number;
  schedule_recurrence?: RecurrenceDefinition;
  schedule_timezone_id?: number | null;
}

// * Auto-Story related interfaces - End

/**
 * Feed client-side concerns. All backend transport lives in `FeedApiService`;
 * what remains here is the per-session operator-hints cache and the feed
 * domain types shared across the app.
 */
@Injectable({
  providedIn: 'root',
})
export class FeedService {
  private feedApi = inject(FeedApiService);

  private operatorsCache: Record<number, Observable<FeedOperatorHint[]>> = {};

  /**
   * Retrieves the operator hints for a given object type ID.
   * Uses service caching to avoid multiple requests for the same object type.
   */
  getFeedOperatorHints(objectTypeId: number): Observable<FeedOperatorHint[]> {
    if (!this.operatorsCache[objectTypeId]) {
      this.operatorsCache[objectTypeId] = this.feedApi
        .getFeedOperatorHints(objectTypeId)
        .pipe(shareReplay(1));
    }
    return this.operatorsCache[objectTypeId];
  }
}
